/**
 * AI Studio request / response schemas (#19).
 *
 * Shared between the API (server-side request validation) and the admin
 * SPA (client-side form validation + TanStack Query hook typing). The
 * schemas live in `shared/` so a typo on either side surfaces at compile
 * time rather than at runtime against a real network call.
 *
 * Endpoints covered:
 *
 *   - POST /api/admin/ai/generate                    — `aiStudioGenerateSchema`
 *   - POST /api/admin/ai/generations/:id/accept      — body-less
 *   - POST /api/admin/ai/generations/:id/reject      — body-less
 *   - GET  /api/admin/ai/usage?month=YYYY-MM         — `aiUsageQuerySchema`
 *   - GET  /api/admin/ai/budget-status               — no body / no query
 *
 * The generate endpoint forwards `prompt` + `options` straight to
 * `getAiProvider().generateText(...)`; the only validation here is shape
 * (non-empty prompt, valid type enum, reasonable temperature / token caps).
 * The provider itself enforces upstream limits.
 */

import { z } from "zod";

/**
 * Mirror of the Prisma `AiGenerationType` enum. Centralised here so Zod can
 * enumerate the literal values at runtime — Prisma enums are types, not
 * runtime values, so we have to repeat the list. Drift is caught by the
 * test suite (`api/tests/ai/studio.integration.test.ts`).
 */
export const aiGenerationTypeSchema = z.enum([
  "PAGE",
  "BLOCK",
  "BLOG_POST",
  "PRODUCT_DESCRIPTION",
  "SEO",
  "IMAGE_PROMPT",
  "DESIGN",
]);
export type AiGenerationTypeInput = z.infer<typeof aiGenerationTypeSchema>;

/**
 * Request body for `POST /api/admin/ai/generate`.
 *
 * `siteId` is required because the budget cap is per-tenant in spirit and
 * the persisted AiGeneration row + activity-log row both denormalise it.
 * `contentItemId` is required: AI Studio always generates *into* a specific
 * target item (the accept flow lands a new DRAFT version on it). An earlier
 * iteration left this optional pending a "pick the target at accept time"
 * flow that was never built — the accept-side `CONTENT_ITEM_REQUIRED` 422
 * proved the half-shipped contract was just a footgun. Tightened now so
 * generate and accept agree end-to-end.
 */
export const aiStudioGenerateSchema = z
  .object({
    siteId: z.string().min(1, "siteId is required"),
    contentItemId: z.string().min(1, "contentItemId is required"),
    /**
     * Persisted on `AiGeneration.type` for audit + AI-Usage-dashboard
     * grouping. AI Studio always targets a single ContentItem so we
     * default to `BLOCK` — the form used to expose this as a dropdown
     * but the seven enum values were decorative (no prompt prefix, no
     * per-type model selection), so the picker was confusing without
     * actually changing the model output. The page-layout AI generator
     * (#181) still passes `type: "PAGE"` from the server side so the
     * two surfaces remain distinguishable in audit / usage views.
     */
    type: aiGenerationTypeSchema.default("BLOCK"),
    prompt: z.string().trim().min(1, "prompt is required").max(8_000),
    /**
     * Free-form options bag forwarded to the provider. We deliberately
     * keep this loose — the provider's own typing pins the keys it
     * actually reads (`temperature`, `maxTokens`, `model`). Anything else
     * is preserved on the AiGeneration row's `inputJson` for replay /
     * audit.
     */
    options: z
      .object({
        model: z.string().min(1).max(128).optional(),
        temperature: z.number().min(0).max(2).optional(),
        maxTokens: z.number().int().positive().max(16_000).optional(),
        /**
         * "Revise" flow: the SPA passes the previous generation's output
         * here so the provider can use it as additional context. Persisted
         * on `inputJson.previousOutput` for traceability.
         *
         * Capped at 8 000 characters to mirror the `prompt` cap above
         * (issue #105). The "Revise" flow concatenates `previousOutput`
         * INTO the prompt sent to the provider, so leaving this unbounded
         * trivially bypasses the prompt cap and lets a single careless
         * paste burn a chunk of the monthly OpenAI budget.
         */
        previousOutput: z.string().max(8_000).optional(),
      })
      .strict()
      .optional(),
  })
  .strict();
/**
 * `AiStudioGenerateInput` is the **wire** shape — what callers send. Uses
 * `z.input` so the `type` default propagates as optional; the SPA can omit
 * it. The post-parse shape (every field defined) is `AiStudioGenerateParsed`
 * — the API service consumes that variant after `safeParse`.
 */
export type AiStudioGenerateInput = z.input<typeof aiStudioGenerateSchema>;
export type AiStudioGenerateParsed = z.infer<typeof aiStudioGenerateSchema>;

/**
 * Response shape for `POST /api/admin/ai/generate`.
 */
export type AiStudioGenerateResponse = {
  generationId: string;
  output: string;
  model: string;
  tokensIn: number;
  tokensOut: number;
  costUsd: number;
};

/**
 * Response shape for `POST /api/admin/ai/generations/:id/accept`.
 *
 * The accept endpoint creates a DRAFT ContentVersion and auto-runs the 5
 * judges. The response carries the version id so the SPA can navigate
 * straight to its edit screen, plus a flag indicating whether this was
 * the first accept (idempotent re-accept returns the previously-created
 * version with `created: false`).
 */
export type AiStudioAcceptResponse = {
  generationId: string;
  contentVersionId: string;
  contentItemId: string;
  /** True on first accept; false when re-accepting an already-accepted generation. */
  created: boolean;
};

/**
 * Response shape for `POST /api/admin/ai/generations/:id/reject`.
 */
export type AiStudioRejectResponse = {
  generationId: string;
  status: "REJECTED";
};

/**
 * Query-parameter schema for `GET /api/admin/ai/usage`. `month` is a
 * `YYYY-MM` string; absence means current UTC month. We validate the
 * format strictly so a typo like `2024-13` (or `2024-1`) surfaces as a
 * 422 rather than silently coercing to an unexpected month.
 */
export const aiUsageQuerySchema = z
  .object({
    month: z
      .string()
      .regex(/^\d{4}-(0[1-9]|1[0-2])$/, "month must be YYYY-MM")
      .optional(),
  })
  .strict();
export type AiUsageQuery = z.infer<typeof aiUsageQuerySchema>;

/**
 * Single row in the `topGenerations` array — matches the columns the
 * Usage page renders. `promptPrefix` is the first 80 characters of the
 * prompt so the table doesn't wrap on long inputs (full prompt available
 * via the future generation-detail screen).
 */
export type AiUsageTopGeneration = {
  id: string;
  type: AiGenerationTypeInput;
  model: string | null;
  costUsd: number;
  createdAt: string;
  promptPrefix: string;
};

/**
 * Single bucket in the `dailySpend` sparkline array. `date` is `YYYY-MM-DD`
 * in UTC; `costUsd` is the sum of `AiGeneration.costUsd` whose `createdAt`
 * falls in that calendar day.
 */
export type AiUsageDailyBucket = {
  date: string;
  costUsd: number;
};

/**
 * Response shape for `GET /api/admin/ai/usage`. All numbers are
 * server-aggregated — the SPA does not compute anything beyond
 * formatting. `periodStart` / `periodEnd` are the [inclusive, exclusive)
 * bounds the server used for the sum.
 */
export type AiUsageResponse = {
  periodStart: string;
  periodEnd: string;
  budgetUsd: number;
  spentUsd: number;
  generationCount: number;
  topGenerations: AiUsageTopGeneration[];
  dailySpend: AiUsageDailyBucket[];
};

/**
 * Response shape for `GET /api/admin/ai/budget-status`. The SPA polls this
 * on every `/ai/*` page so the budget banner can show up the moment the
 * cap is hit (or disappear the moment an override is set).
 */
export type AiBudgetStatusResponse = {
  spentUsd: number;
  budgetUsd: number;
  exceeded: boolean;
  /** ISO instant or null when no override is active for the calling user. */
  overrideExpiresAt: string | null;
};

/* ---------- AI page-layout generator (#181) ---------- */

/**
 * Mirror of `BlockType` (see `../types/index.ts`). The layout-planner is a
 * pure server-side flow so this enum lives in the schema module too — Zod
 * cannot enumerate a type union at runtime. Drift is caught by a compile-
 * time `satisfies z.ZodType<BlockType>` check inside `content.ts` (the
 * other consumer of the same set); the same set is re-listed here rather
 * than re-imported because `content.ts` already imports from `./json.ts`
 * and we want this file to stay self-contained for SPA bundlers.
 */
const blockTypeForLayoutSchema = z.enum([
  "HERO",
  "TEXT",
  "FEATURE_GRID",
  "BENEFITS",
  "PROBLEM_SOLUTION",
  "CTA",
  "FAQ",
  "TESTIMONIALS",
  "PRODUCT_GRID",
  "BLOG_CONTENT",
  "BLOG_LIST",
  "CONTACT_FORM",
  "IMAGE",
  "COMPARISON_TABLE",
  "PRICING_TABLE",
  "TRUST_BADGES",
  "NEWSLETTER",
]);

/**
 * Schema for a single planned block returned by the AI layout planner
 * (issue #181). The provider hands back one of these per block; the
 * page-layout service:
 *
 *   1. Validates the full plan through `blockPlanV1` (this schema's parent
 *      `blockListPlanV1`) BEFORE any write — a malformed plan surfaces as
 *      422 `AI_INVALID_PLAN` so no partial state can land.
 *   2. For each block creates a `ContentItem` (type=PAGE_BLOCK, blockType)
 *      with the supplied `name` and a DRAFT `ContentVersion` carrying the
 *      `contentJson` payload (which is independently validated through the
 *      shared `contentJsonV1` schema).
 *   3. PATCHes the PageVersion's `blocksOrderJson` to reference the new
 *      items in order.
 *
 * The `contentJson` is a passthrough envelope — block-type-specific
 * schemas are out of scope for this slice (the existing edit surface
 * accepts any `schemaVersion: 1` blob too).
 */
export const blockPlanV1 = z
  .object({
    /** Mirrors `ContentItem.blockType`. Constrained to the same enum. */
    blockType: blockTypeForLayoutSchema,
    /**
     * Mirrors `ContentItem.name`. Same length cap as
     * `createContentItemSchema.name` so the planner can't smuggle a 10KB
     * name past the operator's normal create flow.
     */
    name: z.string().trim().min(1).max(200),
    /**
     * Draft `contentJson` for the block's first `ContentVersion`. Re-
     * validated server-side through `contentJsonV1` (see `./json.ts`)
     * before the row lands, so a missing `schemaVersion: 1` field on a
     * planner output is caught here.
     */
    contentJson: z.record(z.string(), z.unknown()),
  })
  .strict();
export type BlockPlanV1 = z.infer<typeof blockPlanV1>;

/**
 * Top-level shape of the AI layout plan. Wrapped in a `schemaVersion: 1`
 * envelope per spec §20 so a future v2 shape has a hinge to swing on.
 *
 * The `blocks` array is capped to match `blocksOrderV1`'s 100-block ceiling
 * (`json.ts:24`) — the planner cannot exceed what the editor itself can
 * persist. A minimum of 1 keeps the success path meaningful; an empty plan
 * is treated as a planner failure (we'd rather 422 than land a no-op).
 */
export const blockListPlanV1 = z
  .object({
    schemaVersion: z.literal(1),
    blocks: z.array(blockPlanV1).min(1).max(100),
  })
  .strict();
export type BlockListPlanV1 = z.infer<typeof blockListPlanV1>;

/**
 * Request body for
 * `POST /api/admin/pages/:pageId/versions/:versionId/ai-generate-layout`.
 *
 * The endpoint is rate-limited + budget-guarded by the same middleware that
 * gates `/api/admin/ai/*` (one provider call per request, regardless of how
 * many blocks the plan contains). The prompt cap matches the AI Studio
 * generate endpoint so the two surfaces share one mental model.
 */
export const aiGenerateLayoutSchema = z
  .object({
    prompt: z.string().trim().min(1, "prompt is required").max(8_000),
  })
  .strict();
export type AiGenerateLayoutInput = z.infer<typeof aiGenerateLayoutSchema>;

/** One newly-created ContentItem reported back to the SPA. */
export type AiGenerateLayoutCreatedBlock = {
  contentItemId: string;
  contentVersionId: string;
  blockType: string;
  name: string;
};

/**
 * Response shape for the AI page-layout generator.
 *
 * `pageVersion` is the freshly-PATCHed PageVersion (the same wire shape the
 * `usePage` query returns under `latestDraft`), so the SPA can swap the
 * cached value without a follow-up GET. `blocks` is the ordered list of
 * newly-created ContentItems so the operator UI can deep-link / highlight
 * each block.
 */
export type AiGenerateLayoutResponse = {
  pageVersionId: string;
  blocks: AiGenerateLayoutCreatedBlock[];
  /** Provider-reported model id. */
  model: string;
  /** Tokens charged on the prompt side. */
  tokensIn: number;
  /** Tokens charged on the completion side. */
  tokensOut: number;
  /** Computed USD cost for the call. Always >= 0. */
  costUsd: number;
};

/* ---------- AI theme generator (#192) ---------- */

/**
 * Request body for `POST /api/admin/sites/:siteId/ai/theme`.
 *
 * The endpoint is rate-limited + budget-guarded by the same middleware that
 * gates `/api/admin/ai/*` (one provider call per request). The prompt cap
 * matches the AI Studio generate endpoint so the surfaces share one mental
 * model — operators can paste a brand brief without re-learning a new ceiling.
 *
 * The endpoint does NOT write to `Site.themeJson` — the operator confirms
 * the proposed theme in the ThemeTab editor and the existing
 * `PATCH /api/admin/sites/:id` endpoint handles persistence.
 */
export const aiGenerateThemeSchema = z
  .object({
    prompt: z.string().trim().min(1, "prompt is required").max(8_000),
  })
  .strict();
export type AiGenerateThemeInput = z.infer<typeof aiGenerateThemeSchema>;

/**
 * Response shape for `POST /api/admin/sites/:siteId/ai/theme`.
 *
 * `themeJson` is the proposed `themeJsonV1` value the operator can apply to
 * the editor. The cost/usage fields mirror the other generation responses so
 * a future "this call cost $0.0001" surface in the dialog has the data
 * available without a follow-up GET against the AI Studio usage endpoint.
 *
 * Typed as `unknown` at the boundary (same convention as `SiteResponse.themeJson`)
 * so SPA callers narrow with `themeJsonV1.safeParse` before consuming —
 * keeps a future v2 shape from breaking older SPAs.
 */
export type AiGenerateThemeResponse = {
  themeJson: unknown;
  /** Provider-reported model id. */
  model: string;
  /** Tokens charged on the prompt side. */
  tokensIn: number;
  /** Tokens charged on the completion side. */
  tokensOut: number;
  /** Computed USD cost for the call. Always >= 0. */
  costUsd: number;
};

/* ---------- AI brand-brief generator (#230) ---------- */

/**
 * Request body for `POST /api/admin/sites/:siteId/ai/brand-brief`.
 *
 * Mirrors `aiGenerateThemeSchema` (#192). The endpoint is rate-limited +
 * budget-guarded by the same middleware that gates `/api/admin/ai/*` (one
 * provider call per request). Prompt cap matches the AI Studio surfaces so
 * operators only have to learn one ceiling.
 *
 * The endpoint does NOT write to `Site.brandJson` — the operator confirms
 * the proposed brief in the BrandBriefSection editor and the existing
 * `PATCH /api/admin/sites/:id` endpoint handles persistence.
 */
export const aiGenerateBrandBriefSchema = z
  .object({
    prompt: z.string().trim().min(1, "prompt is required").max(8_000),
  })
  .strict();
export type AiGenerateBrandBriefInput = z.infer<typeof aiGenerateBrandBriefSchema>;

/**
 * Response shape for `POST /api/admin/sites/:siteId/ai/brand-brief`.
 *
 * `brandJson` is the proposed `brandJsonV1` value the operator can apply to
 * the editor. The cost/usage fields mirror the other generation responses so
 * a future "this call cost $0.0001" surface in the dialog has the data
 * available without a follow-up GET against the AI Studio usage endpoint.
 *
 * Typed as `unknown` at the boundary (same convention as
 * `SiteResponse.brandJson`) so SPA callers narrow with `brandJsonV1.safeParse`
 * before consuming — keeps a future v2 shape from breaking older SPAs.
 */
export type AiGenerateBrandBriefResponse = {
  brandJson: unknown;
  /** Provider-reported model id. */
  model: string;
  /** Tokens charged on the prompt side. */
  tokensIn: number;
  /** Tokens charged on the completion side. */
  tokensOut: number;
  /** Computed USD cost for the call. Always >= 0. */
  costUsd: number;
};
